"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import beforeAfter1 from "../assets/beforeAfter1.png";
import beforeAfter2 from "../assets/beforeAfter2.png";
import beforeAfter3 from "../assets/beforeAfter3.png";
import instagramIcon from "../assets/instagram1.svg";
import logo from "../assets/footer-logo.svg";
import smsIcon from "../assets/sms.svg";
import callIcon from "../assets/call.svg";
import locationIcon from "../assets/location.svg";
import CertificationsSection from "./CertificationsSection";
import { usePathname } from "next/navigation";
import { TRUSTPILOT_PROFILE_URL } from "@/constants/Trustpilot";

const beforeAfterImages = [
  {
    id: 1,
    src: beforeAfter1,
    alt: "Before/After transformation 1",
    hasInstagram: true,
    height: 360,
  },
  {
    id: 2,
    src: beforeAfter2,
    alt: "Before/After transformation 2",
    hasInstagram: true,
    height: 360,
  },
  {
    id: 3,
    src: beforeAfter3,
    alt: "Before/After transformation 3",
    hasInstagram: true,
    height: 360,
  },
  {
    id: 4,
    src: beforeAfter2,
    alt: "Before/After transformation 2",
    hasInstagram: true,
    height: 360,
  },
  {
    id: 5,
    src: beforeAfter3,
    alt: "Before/After transformation 3",
    hasInstagram: true,
    height: 360,
  },
];

const usefulLinks = [
  { name: "Company Policy", href: "/company-policy" },
  { name: "Complaints Policy", href: "/complaints" },
  { name: "Delivery Policy", href: "/delivery" },
  { name: "Privacy Policy", href: "/privacy" },
  { name: "Terms and Conditions", href: "/terms" },
  { name: "FAQs", href: "/faqs" },
  { name: "Prescriber Led Clinic", href: "/about" },
  { name: "Return & Refunds", href: "/returns" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const pathname = usePathname();
  const isHomePage = pathname === "/";

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: "success",
          text: data.message || "Successfully subscribed!",
        });
        setEmail("");

        // Clear success message after 5 seconds
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({
          type: "error",
          text: data.error || "Failed to subscribe. Please try again.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer>
      {/* Certifications Section */}
      <CertificationsSection isHomePage={isHomePage} />

      {/* Before/After Images Section */}
      <div className="bg-gray-100 overflow-hidden py-8">
        <div className="relative">
          <div className="flex animate-marquee">
            {/* First set of images */}
            {beforeAfterImages.map((image) => (
              <div
                key={`first-${image.id}`}
                className="relative group cursor-pointer flex-shrink-0 mx-2"
              >
                <div
                  className="overflow-hidden rounded-2xl relative shadow-lg hover:shadow-2xl transition-all duration-300"
                  style={{
                    height: `${Math.min(image.height, 280)}px`,
                    width: "auto",
                  }}
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    height={Math.min(image.height, 280)}
                    width={0}
                    className="h-full w-auto object-cover transition-transform duration-300 group-hover:scale-105"
                  />

                  {/* Hover Overlay with Instagram Icon */}
                  {image.hasInstagram && (
                    <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center rounded-2xl">
                      <div className="transform scale-75 group-hover:scale-100 transition-transform duration-300">
                        <div className="inline-flex p-3 sm:p-5 items-center gap-2.5 rounded-full bg-gradient-to-br from-[#7771B8] to-purple-500 shadow-xl">
                          <Image
                            src={instagramIcon}
                            alt="Instagram"
                            width={20}
                            height={20}
                            className="w-5 h-5 sm:w-6 sm:h-6"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {/* Duplicate set for seamless loop */}
            {beforeAfterImages.map((image) => (
              <div
                key={`second-${image.id}`}
                className="relative group cursor-pointer flex-shrink-0 mx-2"
              >
                <div
                  className="overflow-hidden rounded-2xl relative shadow-lg hover:shadow-2xl transition-all duration-300"
                  style={{
                    height: `${Math.min(image.height, 280)}px`,
                    width: "auto",
                  }}
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    height={Math.min(image.height, 280)}
                    width={0}
                    className="h-full w-auto object-cover transition-transform duration-300 group-hover:scale-105"
                  />

                  {/* Hover Overlay with Instagram Icon */}
                  {image.hasInstagram && (
                    <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center rounded-2xl">
                      <div className="transform scale-75 group-hover:scale-100 transition-transform duration-300">
                        <div className="inline-flex p-3 sm:p-5 items-center gap-2.5 rounded-full bg-gradient-to-br from-[#7771B8] to-purple-500 shadow-xl">
                          <Image
                            src={instagramIcon}
                            alt="Instagram"
                            width={20}
                            height={20}
                            className="w-5 h-5 sm:w-6 sm:h-6"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative bg-gradient-to-br from-[#0A0F1F] via-[#151a2a] to-[#0A0F1F] text-white overflow-hidden">
        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Company Info - Clean */}
            <div className="lg:col-span-1 space-y-6">
              <div className="flex items-center justify-center lg:justify-start">
                <Image
                  src={logo}
                  alt="UKWeightloss Logo"
                  width={140}
                  height={112}
                  className="sm:w-[160px] sm:h-[128px] filter drop-shadow-lg"
                />
              </div>

              <p className="text-gray-200 text-base leading-relaxed font-light text-center lg:text-left">
                Welcome to the UK Weightloss Company, a dedicated division of
                the Adexcel Aesthetics Clinic committed to revolutionising
                weight loss and management.
              </p>

              {/* Trustpilot link. No score: see constants/Trustpilot.ts */}
              <a
                href={TRUSTPILOT_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
                aria-label="See our reviews on Trustpilot (opens in a new tab)"
              >
                <div className="flex flex-col items-center lg:items-start space-y-2">
                  <span className="text-gray-300 text-sm font-medium">
                    See our reviews on
                  </span>
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-6 h-6 text-[#00B67A] transform group-hover:scale-110 transition-transform duration-200"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                    </svg>
                    <span className="text-white font-bold text-lg group-hover:text-[#00B67A] transition-colors duration-200">
                      Trustpilot
                    </span>
                  </div>
                </div>
              </a>

              {/* Social Media Links */}
              {/* <div className="space-y-3">
                <h5 className="text-white font-medium text-sm">Follow Us</h5>
                <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <a
                    href="#"
                    className="w-9 h-9 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300 rounded-lg"
                  >
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                    </svg>
                  </a>

                  <a
                    href="#"
                    className="w-9 h-9 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300 rounded-lg"
                  >
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
                    </svg>
                  </a>

                  <a
                    href="#"
                    className="w-9 h-9 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300 rounded-lg"
                  >
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.Algeria+" />
                    </svg>
                  </a>

                  <a
                    href="#"
                    className="w-9 h-9 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300 rounded-lg"
                  >
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0188 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9461 2.4189-2.1568 2.4189Z" />
                    </svg>
                  </a>
                </div>
              </div> */}
            </div>

            {/* Useful Links - Simplified */}
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  Useful Links
                </h4>
                <p className="text-gray-400 text-sm font-light">
                  Quick access to essential information
                </p>
              </div>

              <nav className="space-y-1">
                {usefulLinks.map((link) => (
                  <div key={link.name}>
                    <Link
                      href={link.href}
                      className="block text-gray-400 hover:text-white text-sm transition-colors duration-300 py-2 font-light"
                    >
                      {link.name}
                    </Link>
                  </div>
                ))}
              </nav>
            </div>

            {/* Contact Information - Simplified */}
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  Get In Touch
                </h4>
                <p className="text-gray-400 text-sm font-light">
                  We&apos;re here to help with your journey
                </p>
              </div>

              <div className="space-y-4">
                {/* Landline */}
                <div className="flex items-center space-x-3 group">
                  <div className="w-9 h-9 bg-white/10 flex items-center justify-center rounded-lg">
                    <Image
                      src={callIcon}
                      alt="Landline"
                      width={16}
                      height={16}
                      className="w-4 h-4 filter brightness-0 invert opacity-70"
                    />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide font-medium">
                      Landline
                    </p>
                    <p className="text-gray-300 font-light group-hover:text-white transition-colors duration-300">
                      02080580032
                    </p>
                  </div>
                </div>

                {/* Mobile */}
                <div className="flex items-center space-x-3 group">
                  <div className="w-9 h-9 bg-white/10 flex items-center justify-center rounded-lg">
                    <Image
                      src={callIcon}
                      alt="Mobile"
                      width={16}
                      height={16}
                      className="w-4 h-4 filter brightness-0 invert opacity-70"
                    />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide font-medium">
                      Mobile
                    </p>
                    <p className="text-gray-300 font-light group-hover:text-white transition-colors duration-300">
                      07405503752
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center space-x-3 group">
                  <div className="w-9 h-9 bg-white/10 flex items-center justify-center rounded-lg">
                    <Image
                      src={smsIcon}
                      alt="Email"
                      width={16}
                      height={16}
                      className="w-4 h-4 filter brightness-0 invert opacity-70"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-gray-500 text-xs uppercase tracking-wide font-medium">
                      Email
                    </p>
                    <p className="text-gray-300 font-light text-sm group-hover:text-white transition-colors duration-300 break-all">
                      info@ukweightloss.co.uk
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-3 group">
                  <div className="w-9 h-9 bg-white/10 flex items-center justify-center rounded-lg">
                    <Image
                      src={locationIcon}
                      alt="Location"
                      width={16}
                      height={16}
                      className="w-4 h-4 filter brightness-0 invert opacity-70"
                    />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide font-medium mb-1">
                      Location
                    </p>
                    <div className="text-gray-300 font-light text-sm group-hover:text-white transition-colors duration-300">
                      <div>Lamorna Avenue</div>
                      <div>Gravesend Kent DA12 5PT</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Newsletter Subscription */}
            <div className="sm:col-span-2 lg:col-span-1 space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  Stay Updated
                </h4>
                <p className="text-gray-400 text-sm font-light">
                  Join our community for exclusive tips & insights
                </p>
              </div>

              {/* Newsletter Card */}
              <div className="bg-white/5 backdrop-blur-sm p-6 border border-white/10 rounded-xl">
                <div className="space-y-4">
                  <div className="text-center space-y-2">
                    <div className="inline-flex p-2.5 bg-[#7771B8] rounded-lg mb-2">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-white font-semibold text-sm">
                      Weekly Health Tips
                    </p>
                    <p className="text-gray-400 text-sm font-light leading-relaxed">
                      Get expert advice, success stories, and exclusive offers
                      delivered to your inbox.
                    </p>
                  </div>

                  <form onSubmit={handleSubscribe} className="space-y-3">
                    <div>
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-white/20 focus:border-[#7771B8] focus:ring-0 text-white placeholder-gray-500 bg-white/5 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm"
                        required
                      />
                    </div>

                    {/* Success/Error Message */}
                    {message && (
                      <div
                        className={`p-3 rounded-lg border text-sm ${
                          message.type === "success"
                            ? "bg-green-500/10 border-green-500/30 text-green-300"
                            : "bg-red-500/10 border-red-500/30 text-red-300"
                        }`}
                      >
                        <p className="font-light">{message.text}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#7771B8] hover:bg-[#6860a3] text-white py-3 px-6 font-semibold text-sm shadow-md shadow-[#7771B8]/20 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center space-x-2">
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Subscribing...</span>
                        </span>
                      ) : (
                        "Subscribe Now"
                      )}
                    </button>
                  </form>

                  <p className="text-center text-gray-500 text-xs">
                    🔒 We respect your privacy. Unsubscribe anytime.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              {/* Copyright Section */}
              <div className="flex items-center space-x-3">
                <div className="text-center sm:text-left">
                  <div className="text-white font-semibold text-sm">
                    UKWeightloss
                  </div>
                  <div className="text-gray-500 text-xs">
                    Copyright © 2026 - All rights reserved
                  </div>
                  <div className="text-gray-500 text-xs mt-1">
                    Website by{" "}
                    <a
                      href="https://withclinicpro.co.uk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-white transition-colors duration-300"
                    >
                      Clinic Pro
                    </a>
                  </div>
                </div>
              </div>

              {/* Company Details */}
              <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-500">
                <span>Adexcel Limited</span>
                <span className="text-white/20">•</span>
                <span>Reg: 06148484</span>
              </div>
            </div>

            {/* Divider */}
            <div className="mt-6 pt-6 border-t border-white/5">
              <div className="text-center">
                <p className="text-gray-500 text-xs font-light leading-relaxed">
                  Proudly serving the UK with professional weight loss solutions
                  since 2015.
                  <span className="text-gray-400">
                    {" "}
                    CQC Regulated • MHRA Licensed • NMC Registered
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
